import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DEFAULT_EFFORT, EFFORT_LEVELS, supportsEffort, type EffortLevel } from '@shared/protocol';

interface Props {
  model: string;
  effort?: EffortLevel;
  /** What the server offers; the room's own model is added if it was dropped. */
  models: string[];
  /** Only the owner may change either. Everyone else sees a plain chip. */
  canManage: boolean;
  onModel: (model: string) => void;
  onEffort: (effort: EffortLevel) => void;
}

/** claude-haiku-4-5-20251001 -> "Haiku 4.5", opus[1m] -> "Opus (1M)". Anything else as-is. */
export function modelLabel(id: string): string {
  const m = /^(?:claude-)?([a-z]+)((?:-\d{1,2})*)(?:-\d{8})?(\[1m\])?$/i.exec(id);
  if (!m) return id;
  const family = m[1]!.charAt(0).toUpperCase() + m[1]!.slice(1);
  const version = m[2] ? ' ' + m[2].slice(1).replace(/-/g, '.') : '';
  return family + version + (m[3] ? ' (1M)' : '');
}

function modelBlurb(id: string): string {
  if (/opus/i.test(id)) return 'Most capable, for hard problems';
  if (/sonnet/i.test(id)) return 'Fast and capable, for everyday work';
  if (/haiku/i.test(id)) return 'Fastest, for quick answers';
  return '';
}

const EFFORT_LABEL: Record<EffortLevel, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'X-High',
  max: 'Max',
};

const EFFORT_HINT: Record<EffortLevel, string> = {
  low: 'Quickest replies, little thinking',
  medium: 'Some thinking, still quick',
  high: 'Thinks things through — the default',
  xhigh: 'Deeper reasoning, slower turns',
  max: 'Everything it has; slowest and costliest',
};

/** How long the popover takes to fade out before it unmounts. Matches the CSS. */
const CLOSE_MS = 140;

const Chevron = () => (
  <svg className="mp__chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const Check = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

/** Five little bars, lit up to the level. Reads at a glance in the chip. */
function EffortBars({ level, off }: { level: EffortLevel; off?: boolean }) {
  const lit = EFFORT_LEVELS.indexOf(level);
  return (
    <span className={`mp__bars${off ? ' mp__bars--off' : ''}`} aria-hidden>
      {EFFORT_LEVELS.map((l, i) => (
        <span key={l} className={i <= lit ? 'is-lit' : undefined} style={{ height: `${4 + i * 2}px` }} />
      ))}
    </span>
  );
}

/**
 * Top-bar model and effort picker: a chip that opens an animated popover
 * with the model list and an effort slider.
 */
export function ModelPicker({ model, effort = DEFAULT_EFFORT, models, canManage, onModel, onEffort }: Props) {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const hasEffort = supportsEffort(model);

  const close = useCallback(() => {
    setClosing(true);
    window.setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, CLOSE_MS);
  }, []);

  const toggle = () => (open && !closing ? close() : (setClosing(false), setOpen(true)));

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const label = modelLabel(model);

  if (!canManage) {
    return (
      <span className="chip mp__chip mp__chip--static" title="Model and effort — only the room owner can change them">
        {label}
        {hasEffort && <EffortBars level={effort} />}
      </span>
    );
  }

  // A model dropped from CLAUDE_MODELS stays listed while the room uses it.
  const list = models.includes(model) ? models : [model, ...models];

  return (
    <div className="mp" ref={rootRef}>
      <button
        className="chip mp__chip"
        onClick={toggle}
        aria-expanded={open && !closing}
        aria-haspopup="dialog"
        title={hasEffort ? `${label} · ${EFFORT_LABEL[effort]} effort` : label}
      >
        {label}
        {hasEffort && <EffortBars level={effort} />}
        <Chevron />
      </button>

      {open && (
        <div className={`mp__pop${closing ? ' is-closing' : ''}`} role="dialog" aria-label="Model and effort">
          <ModelList models={list} value={model} onPick={onModel} />
          <div className="mp__divider" />
          <EffortSlider value={effort} disabled={!hasEffort} onChange={onEffort} />
        </div>
      )}
    </div>
  );
}

function ModelList({ models, value, onPick }: { models: string[]; value: string; onPick: (m: string) => void }) {
  const listRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState<{ top: number; height: number }>();

  // A highlight that glides to the selected row instead of jumping.
  useLayoutEffect(() => {
    const row = listRef.current?.querySelector<HTMLElement>('[aria-checked="true"]');
    if (row) setPill({ top: row.offsetTop, height: row.offsetHeight });
  }, [value, models]);

  return (
    <div className="mp__section">
      <div className="mp__heading">Model</div>
      <div className="mp__models" role="radiogroup" aria-label="Model" ref={listRef}>
        {pill && <span className="mp__pill" style={{ transform: `translateY(${pill.top}px)`, height: pill.height }} />}
        {models.map((m) => (
          <button
            key={m}
            role="radio"
            aria-checked={m === value}
            className="mp__model"
            onClick={() => m !== value && onPick(m)}
          >
            <span className="mp__model-text">
              <span className="mp__model-name">{modelLabel(m)}</span>
              {modelBlurb(m) && <span className="mp__model-blurb">{modelBlurb(m)}</span>}
            </span>
            <span className="mp__check">{m === value && <Check />}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function EffortSlider({
  value,
  disabled,
  onChange,
}: {
  value: EffortLevel;
  disabled: boolean;
  onChange: (e: EffortLevel) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<number>();
  // Holds the pick until the server's echo arrives, so the thumb does not
  // snap back for a round trip.
  const [optimistic, setOptimistic] = useState<EffortLevel>();
  useEffect(() => setOptimistic(undefined), [value]);

  const last = EFFORT_LEVELS.length - 1;
  const index = drag ?? EFFORT_LEVELS.indexOf(optimistic ?? value);
  const shown = EFFORT_LEVELS[index]!;
  const pct = (index / last) * 100;

  const commit = (i: number) => {
    const next = EFFORT_LEVELS[Math.max(0, Math.min(last, i))]!;
    if (next === (optimistic ?? value)) return;
    setOptimistic(next);
    onChange(next);
  };

  const indexAt = (clientX: number) => {
    const rect = trackRef.current!.getBoundingClientRect();
    return Math.round(Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * last);
  };

  return (
    <div className={`mp__section mp__effort${disabled ? ' is-disabled' : ''}`}>
      <div className="mp__heading mp__heading--row">
        <span>Effort</span>
        <span className="mp__effort-value" key={shown}>
          {disabled ? 'n/a' : EFFORT_LABEL[shown]}
        </span>
      </div>

      <div
        className={`mp__slider${drag !== undefined ? ' is-dragging' : ''}`}
        ref={trackRef}
        onPointerDown={(e) => {
          if (disabled) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          setDrag(indexAt(e.clientX));
        }}
        onPointerMove={(e) => {
          if (drag !== undefined) setDrag(indexAt(e.clientX));
        }}
        onPointerUp={(e) => {
          if (drag === undefined) return;
          commit(indexAt(e.clientX));
          setDrag(undefined);
        }}
        onPointerCancel={() => setDrag(undefined)}
      >
        <div className="mp__track">
          <div className="mp__fill" style={{ width: `${pct}%` }} />
        </div>
        {EFFORT_LEVELS.map((l, i) => (
          <span
            key={l}
            className={`mp__stop${i <= index ? ' is-lit' : ''}`}
            style={{ left: `${(i / last) * 100}%` }}
            aria-hidden
          />
        ))}
        <div
          className="mp__thumb"
          style={{ left: `${pct}%` }}
          role="slider"
          tabIndex={disabled ? -1 : 0}
          aria-label="Effort"
          aria-valuemin={0}
          aria-valuemax={last}
          aria-valuenow={index}
          aria-valuetext={EFFORT_LABEL[shown]}
          aria-disabled={disabled}
          onKeyDown={(e) => {
            if (disabled) return;
            const step = { ArrowLeft: -1, ArrowDown: -1, ArrowRight: 1, ArrowUp: 1 }[e.key];
            if (step !== undefined) commit(index + step);
            else if (e.key === 'Home') commit(0);
            else if (e.key === 'End') commit(last);
            else return;
            e.preventDefault();
          }}
        />
      </div>

      <div className="mp__ticks">
        {EFFORT_LEVELS.map((l, i) => (
          <button
            key={l}
            className={i === index ? 'is-active' : undefined}
            style={{ left: `${(i / last) * 100}%` }}
            disabled={disabled}
            onClick={() => commit(i)}
            tabIndex={-1}
          >
            {EFFORT_LABEL[l]}
          </button>
        ))}
      </div>

      <p className="mp__hint">
        {disabled ? 'This model does not take an effort setting.' : EFFORT_HINT[shown]}
      </p>
    </div>
  );
}
